import { useLocalSearchParams } from 'expo-router';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { Box, Icon, Text as UCLText } from '@fhs-legacy/universal-components';
import ActionButton from 'components/action-button';
import Modal, { ModalContent, ModalHeading } from 'components/modal';
import { useOrderDetails } from 'hooks/use-order-details';
import { useCRMEventsContext } from 'state/crm-events';
import { ErrorDialogViewComponentNames } from 'state/crm-events/constants';
import { useOrderContext } from 'state/order';
import { useReorder } from 'state/order/hooks/use-reorder';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { isDelivery } from 'utils/service-mode';

const Container = Box.withConfig({
  alignItems: 'center',

  _text: {
    textAlign: 'center',
  },

  paddingTop: '$8',
  paddingRight: 0,
  paddingBottom: '$12',
  paddingLeft: 0,
  margin: 'auto',

  maxWidth: {
    base: 300,
    desktop: 500,
  },
});

const Heading = ModalHeading.withConfig({
  marginTop: '$12',
  marginRight: 0,
  marginBottom: '$4',
  marginLeft: 0,
});

const Text = UCLText.withConfig({
  marginTop: 0,
  marginRight: 0,
  marginBottom: '$12',
  marginLeft: 0,
});

const ErrorIcon = Icon.withConfig({
  variant: 'error',
  // TODO: RN
  // color:"primary",
  width: '$32',
  height: '$32',
});

interface IOrderCancelledModalProps {
  onDismiss: () => void;
  modalAppearanceEventMessage?: string;
}

const OrderCancelledModal: React.FC<React.PropsWithChildren<IOrderCancelledModalProps>> = ({
  onDismiss,
  modalAppearanceEventMessage = 'Order Cancelled',
}) => {
  const {
    serverOrder,
    reorder: { reordering },
    serviceMode,
  } = useOrderContext();
  const params = useLocalSearchParams();
  const rbiOrderId = serverOrder.rbiOrderId || params.rbiOrderId;
  const { order } = useOrderDetails(rbiOrderId);
  const hideReorderButton =
    Boolean(order?.delivery) || order?.order?.hasUnavailableItem || isDelivery(serviceMode);
  const { handleReorderClick } = useReorder(order);
  const { formatMessage } = useIntl();
  const { logErrorDialogView } = useCRMEventsContext();

  const modalHeading = formatMessage({ id: 'cancelledOrderTitle' });
  const modalBody = formatMessage({ id: 'cancelledOrderAction' });

  const handlePress = () => {
    handleReorderClick();
  };

  useEffect(() => {
    logErrorDialogView(
      ErrorDialogViewComponentNames.ORDER_CANCELLED_MODAL,
      modalAppearanceEventMessage
    );
  });

  return (
    <Modal
      onDismiss={onDismiss}
      accessibilityLabel={modalHeading}
      mParticleEventData={{
        modalAppearanceEventMessage,
        modalMessage: modalBody,
      }}
    >
      <ModalContent>
        <Container>
          <ErrorIcon {...hiddenAccessibilityPlatformProps} />
          <Heading>{modalHeading}</Heading>
          <Text>{modalBody}</Text>
          {!hideReorderButton && (
            <ActionButton onPress={handlePress} isLoading={reordering} disabled={reordering}>
              {formatMessage({ id: 'reorder' })}
            </ActionButton>
          )}
        </Container>
      </ModalContent>
    </Modal>
  );
};

export default OrderCancelledModal;
